<template>
  <div class="effect-builder">
    <div v-if="localEffects.length > 0" class="effect-builder__list">
      <div
        v-for="(effect, index) in localEffects"
        :key="index"
        class="effect-builder__card"
      >
        <div class="effect-builder__card-header">
          <span class="effect-builder__card-type">{{ getEffectLabel(effect.type) }}</span>
          <button
            v-if="isEdit"
            class="effect-builder__remove-btn"
            @click="removeEffect(index)"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="effect-builder__card-content">
          <div class="effect-builder__row">
            <Input
              :inputData="effectTypeInput"
              :model="effect.type"
              :isEdit="isEdit"
              @updateModel="updateEffectField(index, 'type', $event)"
              customStyle="flex: 1; min-width: 150px;"
            />

            <Input
              v-if="effect.type !== 'FREE'"
              :key="'value-' + index + '-' + effect.type"
              :inputData="getValueInput(effect.type)"
              :model="effect.value"
              :isEdit="isEdit"
              @updateModel="updateEffectField(index, 'value', $event)"
              customStyle="flex: 1; min-width: 150px;"
            />
          </div>

          <div class="effect-builder__row">
            <Input
              v-if="discountType !== 'bundle'"
              :inputData="maxQuantityInput"
              :model="effect.maxQuantity"
              :isEdit="isEdit"
              @updateModel="updateEffectField(index, 'maxQuantity', $event)"
              customStyle="flex: 1; min-width: 150px;"
            />

            <Input
              :inputData="applyToInput"
              :model="effect.applyTo"
              :isEdit="isEdit"
              @updateModel="updateEffectField(index, 'applyTo', $event)"
              customStyle="flex: 1; min-width: 150px;"
            />
          </div>

          <!-- Target Conditions (for BOGO style effects) -->
          <div v-if="effect.targetConditions" class="effect-builder__target-section">
            <div class="effect-builder__target-header">
              <div class="effect-builder__target-label">Target Conditions <br/><i>  For BOGO & Free Item effects, Max Effect Applications controls quantity of products to discount</i></div>
              <button
                v-if="isEdit"
                class="effect-builder__toggle-target-btn"
                @click="removeTargetConditions(index)"
              >
                Remove
              </button>
            </div>
            <ConditionBuilder
              :conditions="effect.targetConditions"
              :isEdit="isEdit"
              @update="updateTargetConditions(index, $event)"
            />
          </div>
          <button
            v-else-if="isEdit"
            class="effect-builder__add-target-btn"
            @click="addTargetConditions(index)"
          >
            <i class="fa fa-plus"></i> Add Target Conditions (for BOGO)
          </button>
        </div>
      </div>
    </div>

    <div v-else class="effect-builder__empty">
      <span>No effects defined. Add an effect to specify the discount.</span>
    </div>

    <button
      v-if="isEdit"
      class="effect-builder__add-btn"
      @click="addEffect"
    >
      <i class="fa fa-plus"></i> Add Effect
    </button>
  </div>
</template>

<script>
import ConditionBuilder from "./ConditionBuilder.vue";
import Input from "@/components/Input/Input.vue";
import { effectTypeOptions, applyToOptions } from "../inputs";

export default {
  name: "EffectBuilder",
  components: {
    ConditionBuilder,
    Input,
  },
  props: {
    effects: {
      type: Array,
      default: () => [],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    discountType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localEffects: JSON.parse(JSON.stringify(this.effects || [])),
    };
  },
  watch: {
    effects: {
      handler(newVal) {
        this.localEffects = JSON.parse(JSON.stringify(newVal || []));
      },
      deep: true,
    },
  },
  computed: {
    effectTypeInput() {
      return {
        id: "type",
        type: "select",
        title: "Effect Type",
        placeholder: "Select Type",
        options: effectTypeOptions,
      };
    },
    maxQuantityInput() {
      return {
        id: "maxQuantity",
        type: "number",
        title: "Max Effect Applications (For unlimited applications, set to 0)",
        placeholder: "1",
        precision: 0,
      };
    },
    applyToInput() {
      return {
        id: "applyTo",
        type: "select",
        title: "Apply To",
        placeholder: "Select Order",
        options: applyToOptions,
      };
    },
  },
  methods: {
    getValueInput(effectType) {
      if (effectType === "PERCENT_OFF") {
        return {
          id: "value",
          type: "number",
          title: "Percentage",
          placeholder: "0",
          precision: 2,
          suffix: "%",
        };
      }
      return {
        id: "value",
        type: "money",
        title: "Amount",
        placeholder: "0.00",
        prefix: "$",
      };
    },

    emitUpdate() {
      this.$emit("update", this.localEffects);
    },

    updateEffectField(index, field, value) {
      this.localEffects[index][field] = value;
      this.emitUpdate();
    },

    addEffect() {
      this.localEffects.push({
        type: "PERCENT_OFF",
        value: 0,
        maxQuantity: 1,
        applyTo: "all",
        targetConditions: null,
      });
      this.emitUpdate();
    },

    removeEffect(index) {
      this.localEffects.splice(index, 1);
      this.emitUpdate();
    },

    addTargetConditions(index) {
      this.localEffects[index].targetConditions = {
        type: "AND",
        rules: [],
      };
      this.emitUpdate();
    },

    removeTargetConditions(index) {
      this.localEffects[index].targetConditions = null;
      this.emitUpdate();
    },

    updateTargetConditions(index, conditions) {
      this.localEffects[index].targetConditions = conditions;
      this.emitUpdate();
    },

    getEffectLabel(type) {
      const labels = {
        PERCENT_OFF: "Percent Off",
        DOLLAR_OFF: "Dollar Off",
        FIXED_PRICE: "Fixed Price",
        FREE: "Free",
      };
      return labels[type] || type;
    },
  },
};
</script>

<style lang="scss" scoped>
.effect-builder {
  &__list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  &__card {
    background: rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 8px;
  }

  &__card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background: rgba(16, 185, 129, 0.1);
    border-bottom: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 8px 8px 0 0;
  }

  &__card-type {
    font-size: 13px;
    font-weight: 600;
    color: #10b981;
  }

  &__card-content {
    padding: 1rem;
  }

  &__row {
    position: relative;
    // Earlier rows stack above later ones so an open select menu is not
    // covered by the fields beneath it.
    z-index: 2;
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;

    & + & {
      z-index: 1;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__remove-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: rgba(239, 68, 68, 0.15);
    border: none;
    border-radius: 4px;
    color: #ef4444;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(239, 68, 68, 0.3);
    }
  }

  &__target-section {
    margin-top: 1rem;
    padding: 1rem;
    background: rgba(96, 165, 250, 0.05);
    border: 1px dashed rgba(96, 165, 250, 0.3);
    border-radius: 6px;
  }

  &__target-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__target-label {
    font-size: 12px;
    font-weight: 500;
    color: #60a5fa;
  }

  &__toggle-target-btn {
    font-size: 11px;
    padding: 4px 8px;
    background: rgba(239, 68, 68, 0.1);
    border: none;
    border-radius: 4px;
    color: #ef4444;
    cursor: pointer;

    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  }

  &__add-target-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    padding: 6px 12px;
    font-size: 11px;
    background: transparent;
    border: 1px dashed rgba(96, 165, 250, 0.3);
    color: #60a5fa;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(96, 165, 250, 0.1);
    }
  }

  &__empty {
    text-align: center;
    padding: 2rem;
    color: rgba(255, 255, 255, 0.5);
    font-size: 13px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  &__add-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 10px 20px;
    font-size: 13px;
    background: rgba(16, 185, 129, 0.1);
    border: 1px dashed rgba(16, 185, 129, 0.4);
    color: #10b981;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(16, 185, 129, 0.2);
    }
  }
}

// Light theme overrides
.light .effect-builder {
  &__card {
    background: rgba(16, 185, 129, 0.05);
  }

  &__card-header {
    background: rgba(16, 185, 129, 0.12);
  }

  &__target-section {
    background: rgba(96, 165, 250, 0.08);
  }

  &__empty {
    color: #7f8c8d;
    background: rgba(0, 0, 0, 0.03);
  }
}
</style>
